import { clamp, clamp01 } from "../math/Geometry.js";
import type { Vec2 } from "../math/Vec2.js";
import { tuning } from "../state/tuning.js";
import type { GoalieState } from "../state/types.js";

const DT = 1 / 60;

export function tickGoalie(
  goalie: GoalieState,
  puckPos: Vec2,
  manualInput: { moveX: number; moveY: number } | null,
): void {
  if (goalie.pulled) {
    goalie.vel.x = 0;
    goalie.vel.y = 0;
    if (goalie.modeTimer > 0) goalie.modeTimer -= 1;
    if (goalie.mode === "Saving" && goalie.modeTimer <= 0) {
      goalie.mode = "Recovering";
      goalie.modeTimer = tuning.goalie.reactionTicks;
    } else if (goalie.mode === "Recovering" && goalie.modeTimer <= 0) {
      goalie.mode = "Ready";
    }
    return;
  }
  if (manualInput !== null && goalie.mode !== "Saving") {
    tickGoalieManual(goalie, puckPos, manualInput);
  } else {
    tickGoalieAuto(goalie, puckPos);
  }

  // Mode FSM timer tick — runs regardless of branch.
  if (goalie.modeTimer > 0) goalie.modeTimer -= 1;
  if (goalie.mode === "Saving" && goalie.modeTimer <= 0) {
    goalie.mode = "Recovering";
    goalie.modeTimer = tuning.goalie.reactionTicks;
  } else if (goalie.mode === "Recovering" && goalie.modeTimer <= 0) {
    goalie.mode = "Ready";
  }
}

function tickGoalieAuto(goalie: GoalieState, puckPos: Vec2): void {
  const halfL = tuning.rink.length / 2;
  const sign = goalie.teamId === "home" ? -1 : 1;
  const goalX = sign * (halfL - tuning.rink.goalLineOffset);

  // Depth based on puck x-distance from goal.
  const puckDist = Math.abs(puckPos.x - goalX);
  const tDepth = clamp01(puckDist / tuning.goalie.arcDepthPuckMaxDistance);
  const depth = tuning.goalie.arcDepthFar * tDepth + tuning.goalie.arcDepthNear * (1 - tDepth);
  const targetX = goalX - sign * depth;

  // Lateral target on the line from goal center to puck, scaled to the depth.
  const lineDx = puckPos.x - goalX;
  const lineDy = puckPos.y;
  const lineLen = Math.sqrt(lineDx * lineDx + lineDy * lineDy);
  let targetY = 0;
  if (lineLen > 0.01) {
    targetY = (lineDy / lineLen) * depth;
  }
  const halfMouth = tuning.rink.goalMouthHalfWidth;
  targetY = clamp(targetY, -halfMouth, halfMouth);

  // Move at capped speed toward (targetX, targetY).
  const dx = targetX - goalie.pos.x;
  const dy = targetY - goalie.pos.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const maxStep = tuning.goalie.lateralMaxSpeed * DT;
  if (dist < 0.001) {
    goalie.vel.x = 0;
    goalie.vel.y = 0;
  } else if (dist <= maxStep) {
    goalie.vel.x = dx / DT;
    goalie.vel.y = dy / DT;
    goalie.pos.x = targetX;
    goalie.pos.y = targetY;
  } else {
    const stepX = (dx / dist) * maxStep;
    const stepY = (dy / dist) * maxStep;
    goalie.vel.x = stepX / DT;
    goalie.vel.y = stepY / DT;
    goalie.pos.x += stepX;
    goalie.pos.y += stepY;
  }

  // Facing toward puck.
  if (lineLen > 0.001) {
    goalie.facing.x = lineDx / lineLen;
    goalie.facing.y = lineDy / lineLen;
  }
}

function tickGoalieManual(
  goalie: GoalieState,
  puckPos: Vec2,
  input: { moveX: number; moveY: number },
): void {
  const t = tuning.goalie;
  const mag = Math.sqrt(input.moveX * input.moveX + input.moveY * input.moveY);
  let vx = 0;
  let vy = 0;
  if (mag > 0.001) {
    const inputMag = Math.min(1, mag);
    vx = (input.moveX / mag) * t.manualMaxSpeed * inputMag;
    vy = (input.moveY / mag) * t.manualMaxSpeed * inputMag;
  }
  goalie.vel.x = vx;
  goalie.vel.y = vy;
  goalie.pos.x += vx * DT;
  goalie.pos.y += vy * DT;

  // Clamp position to the goalie box.
  const halfL = tuning.rink.length / 2;
  const sign = goalie.teamId === "home" ? -1 : 1;
  const goalX = sign * (halfL - tuning.rink.goalLineOffset);
  const farX = goalX - sign * t.manualBoxForward;
  const xMin = Math.min(goalX, farX);
  const xMax = Math.max(goalX, farX);
  goalie.pos.x = clamp(goalie.pos.x, xMin, xMax);
  goalie.pos.y = clamp(goalie.pos.y, -t.manualBoxHalfWidth, t.manualBoxHalfWidth);

  // Facing toward puck (preserve save-volume orientation behavior).
  const lineDx = puckPos.x - goalX;
  const lineDy = puckPos.y;
  const lineLen = Math.sqrt(lineDx * lineDx + lineDy * lineDy);
  if (lineLen > 0.001) {
    goalie.facing.x = lineDx / lineLen;
    goalie.facing.y = lineDy / lineLen;
  }

  // Promote Ready → Tracking for symmetry with the FSM branch.
  if (goalie.mode === "Ready") goalie.mode = "Tracking";
}
